import React, {useEffect, useState, useTransition} from "react";
import {useViewerStore} from "../../../../../../store/store";
import {Badge, Button, Flex, InputNumber, Select, Spin, Tag, Tooltip} from 'antd';
import {useCalculationStates} from "../../../../../../hooks/calculationStateHook";
import {MODULE_GRAPH, siteCalculationState} from "../../../../../../services/siteCalculationState";
import {useCurrentUser} from "../../../../../../contexts/UserContext";
import { RoofInformationTableProps, RoofMesh } from "../../../../../../@types/types";
import { CalculationType } from "../../../../../../@types/enums";
import ProductionLoader from "./ProductionLoader";
import {InfoCircleOutlined, InfoOutlined} from "@ant-design/icons";
import PitchOverrideInfoTable from "../../../Modal/Modal-Content/PitchOverrideInfoTable";
import {isGroundMount} from "../../../Three-JS-Viewer/Controllers/PlaneRoofControls";

const RoofInformationTable: React.FC<RoofInformationTableProps> = ({shortened}) => {
    const trueShortened = shortened ? shortened : window.innerWidth <= 1366;

    const {hasCapability} = useCurrentUser();
    const [isPending, startTransition] = useTransition();
    const [editablePitch, setEditablePitch] = useState<any>({});
    const [currentEditingValue, setCurrentEditingValue] = useState<any>({});
    const pitchDisplayMode = useViewerStore(state => state.pitchDisplayMode);
    const sceneViewer = useViewerStore(state => state.sceneViewer);
    const roofInfoArr = useViewerStore(state => state.roofInfoArr);
    const modeCount33Arr = useViewerStore(state => state.buildingWise33PercentRuleModuleCountList);
    const accessoryBuildingList = useViewerStore(state => state.accessoryBuildingList);
    const {needsUpdate} = useCalculationStates([CalculationType.ACCESS_MULTI, CalculationType.IDEAL_MULTI, CalculationType.MODULE_GRAPH]);
    const isPitchOverrideByUser = useViewerStore(state => state.isPitchOverrideByUser);
    const designList = useViewerStore(state => state.designList);
    const detachedStructureBuildingIds = useViewerStore(state => state.detachedStructureBuildingIds);
    const [roofPitchData, setRoofPitchData] = React.useState<Array<{
        key: number;
        roofId:  number;
        originalPitch: number;
        overridePitch: number;
    }>>([])

    useEffect(() => {
        if (needsUpdate[MODULE_GRAPH]) {
            startTransition(() => {
                siteCalculationState.useCalculations(CalculationType.MODULE_GRAPH);
            });
        }
    }, [needsUpdate]);



    const thArr = [
        {key: 'roofNum', label: 'Roof ID', des: 'Roof'}, // Roof column
        {key: 'azimuth', label: 'Azimuth', des: 'Azimuth'},
        {key: 'solarAccess', label: 'Solar Access', des: 'Solar Access'},
        {key: 'efficiency', label: 'Efficiency', des: 'Efficiency'},
        {key: 'modules', label: 'Modules', des: 'Modules'},
        {key: 'arraySize', label: 'Array Size', des: 'Array Size'},
        ...(hasCapability('Monthly_Estimate') && !trueShortened) ? [
            {key: 'utility_production', label: 'Utility Production', des: 'Utility Production'},
            {key: 'actual_production', label: 'Actual Production', des: 'Actual Production'}
        ] : []
    ];

    const accessoryRoofs: any[] = [];

    if (sceneViewer) {
        for (const roof of sceneViewer.roofMeshArr) {
            if (accessoryBuildingList.includes(roof.userData.buildingName)) {
                accessoryRoofs.push(roof.roofFaceId);
            }
        }
    }

    const roofSplitIntoBuilding = roofInfoArr.reduce((acc: any, roof: any) => {
        const buildingName = roof.buildingName;
        if (!acc[buildingName]) {
            acc[buildingName] = [];
        }
        acc[buildingName].push(roof);
        acc[buildingName].modeCount33 = modeCount33Arr?.find((b) => b.name === buildingName)?.modeCount33;
        return acc;
    }, {});


    const roofSplitIntoBuildingArr = Object.keys(roofSplitIntoBuilding).map((buildingName) => roofSplitIntoBuilding[buildingName]);

    const handleDoubleClick = (roofFaceId: string, currentPitch: number) => {
        if (!editablePitch[roofFaceId]) {
            setEditablePitch((prev: any) => ({
                ...prev,
                [roofFaceId]: true
            }));
            setCurrentEditingValue((prev: any) => ({
                ...prev,
                [roofFaceId]: currentPitch
            }));
        }
    };

    const handlePitchChange = (value: number, roofFaceId: string) => {
        setCurrentEditingValue((prev: any) => ({
            ...prev,
            [roofFaceId]: value
        }));
    };

    const updatePitch = (roofFaceId: string, newPitch: number, roofTagId: number) => {
        const roundedNewPitch = Math.round(newPitch);

        const updatedRoofInfoArr = roofInfoArr.map((roof) =>
            roof.roofFaceId === roofFaceId ? {...roof, pitchOverride: roundedNewPitch} : roof
        );
        const roofInMeshArr = sceneViewer.roofMeshArr.find((roof: RoofMesh) => roof.roofFaceId === roofFaceId);
        if (roofInMeshArr) {
            roofInMeshArr.userData.pitchOverride = roundedNewPitch;
        }
        sceneViewer.updatePitchOverrideText(roofTagId, roundedNewPitch);

        useViewerStore.setState({roofInfoArr: updatedRoofInfoArr, isPitchOverrideByUser: true});
    };

    const handleBlur = (roofFaceId: string, originalPitch: number, roofTagId: number, curentValue: number) => {
        const newPitch = Math.round(currentEditingValue[roofFaceId]);

        // if (newPitch === Math.round(originalPitch)) {
        //     // No changes, so no need to show alert
        //     setEditablePitch((prev) => ({
        //         ...prev,
        //         [roofFaceId]: false
        //     }));
        //     return;
        // }

        const minValue = Math.round(originalPitch) - 5;
        const maxValue = Math.round(originalPitch) + 5;

        if (newPitch < minValue || newPitch > maxValue) {
            sceneViewer.viewerAlert({
                show: true,
                title: 'Limit Exceeded',
                message: (
                    <>
                        <p>Error: Cannot set pitch to {newPitch}.</p>
                        <br/>
                        <p>Pitch can be adjusted only by ± 5 degrees, Pitch value should be
                            between {minValue} and {maxValue}</p>
                        <br/>
                        <p>Original Pitch: {Math.round(originalPitch)}</p>
                    </>
                ),
                messageType: 'error',
                isCloseAfterDisplay: true,
            })
            setCurrentEditingValue((prev: any) => ({
                ...prev,
                [roofFaceId]: originalPitch
            }));
            setEditablePitch((prev: any) => ({
                ...prev,
                [roofFaceId]: false
            }));


            return;
        }

        sceneViewer.viewerAlert({
            title: 'Roof Pitch Override',
            message: (
                <>
                    <p>Are you sure you want to overwrite the current pitch value for roof {roofTagId}? To continue,
                        click "Confirm". To undo your changes, click "Cancel."</p>
                    <br/>
                    <p>Original Pitch: {Math.round(originalPitch)} → Previous Adjusted
                        Pitch: {Math.round(curentValue)} → New Pitch: {newPitch}</p>
                    <br/>
                    <div className={"modal-bottom-action-button-container"}>
                        <Button
                            className={"editor__control--finalize modal-confirm-button"}
                            type="primary"
                            onClick={() => {
                                sceneViewer.viewerAlert({show: false});
                                updatePitch(roofFaceId, newPitch, roofTagId);
                            }}
                        >
                            Confirm
                        </Button>
                        <Button
                            className="editor__control--finalize modal-cancel-button"
                            onClick={() => {
                                sceneViewer.viewerAlert({show: false});
                                setCurrentEditingValue((prev: any) => ({
                                    ...prev,
                                    [roofFaceId]: originalPitch
                                }));
                            }}
                        >
                            Cancel
                        </Button>
                    </div>
                </>
            ),
            messageType: 'info',
            isModal: true,
        });

        setEditablePitch((prev: any) => ({
            ...prev,
            [roofFaceId]: false
        }));
    };

    const onClickRoofPitchOverrideInfo = () => {
        const newData = sceneViewer.roofMeshArr
            .filter((roofMesh: RoofMesh) => {
                const originalPitch = roofMesh.oriAng.tilt;
                const overriddenPitch = roofMesh.userData.pitchOverride;
                const roofId = roofMesh.userData.roof_tag_id;

                return overriddenPitch !== undefined &&
                       overriddenPitch !== originalPitch &&
                       !roofPitchData.some(roof => roof.roofId === roofId);
            })
            .map((roofMesh: RoofMesh) => ({
                key: roofMesh.userData.roof_tag_id,
                roofId: roofMesh.userData.roof_tag_id,
                originalPitch: roofMesh.oriAng.tilt,
                overridePitch: roofMesh.userData.pitchOverride,
            }));

        const updatedData = [...roofPitchData, ...newData];

        if (newData.length > 0) {
            setRoofPitchData(updatedData);
        }

        sceneViewer.viewerAlert({
            show: true,
            isModal: true,
            title: "Pitch Override Info",
            message: <PitchOverrideInfoTable roofPitchData={updatedData} />,
            messageType: "info"
        });
    };


    return (
        <div className={"tab-content-container"}>
            <div className={"tab-content-scrollable-container"}>
                {isPending || Object.values(needsUpdate).includes(true) ? (
                    <ProductionLoader isPopoutVersion={!shortened}/>
                ) : (
                    <>
                        {
                            isPitchOverrideByUser && (
                                <Flex
                                    gap={10}
                                >
                                    <Badge status="success" text={"Pitch is adjusted"}/>
                                    <Tooltip title="=Click to show overriden roof pitch data" placement={"right"}>
                                        <InfoCircleOutlined onClick={onClickRoofPitchOverrideInfo} />
                                    </Tooltip>

                                    <span></span>
                                </Flex>


                            )
                        }
                        <table className={"tab__table"}>
                            <thead>
                            <tr>
                                {/* Roof Column */}
                                <th key="roofNum">{
                                    trueShortened ? 'R' : 'Roof'

                                }</th>
                                {/* Pitch Column */}
                                <th key="pitch">

                                <span
                                    className={"pitchOverrideLabel"}
                                >
                                    {trueShortened ? 'P' : 'Pitch'}
                                </span>
                                    <Select
                                        className={`pitchOverrideSelect ${trueShortened ? 'adjustPitchColumnWidth' : ''}`}
                                        value={pitchDisplayMode}
                                        onChange={(value) => useViewerStore.setState({pitchDisplayMode: value})}
                                        options={[
                                            {value: 'Pitch', label: 'Original'},
                                            {value: 'Pitch Override', label: 'Adjusted'},
                                        ]}
                                    />
                                </th>
                                {thArr.filter((item) => item.key !== 'roofNum').map((item) => (
                                    <th key={item.des}>{trueShortened ? String(item.des)[0] : item.des}</th>
                                ))}
                            </tr>
                            </thead>
                            <tbody>
                            {roofSplitIntoBuildingArr.map((buildingWiseArr, idx) => (
                                buildingWiseArr
                                    .filter((i: any) => !accessoryRoofs.includes(i.roofFaceId) && Number(i.arraySize) > 0)
                                    .map((data: any, index: number) => (
                                        <React.Fragment key={`${idx}-${data.roofFaceId}`}>
                                            {index === 0 && (
                                                <tr key={`building-${idx}`} className="building-item">
                                                    <td colSpan={thArr.length + 1} style={{textAlign: "left"}}>
                                                        <label>Building {data.buildingName}
                                                            &nbsp;
                                                            {
                                                                isGroundMount(sceneViewer.roofMeshArr.find((roof) => roof.roofFaceId === data.roofFaceId)) ? (
                                                                    <Tag color="blue">Ground Mount</Tag>
                                                                )
                                                            :
                                                            <>
                                                                - 33% Modules: { ( isNaN(buildingWiseArr.modeCount33) || buildingWiseArr.modeCount33===undefined ) ? <Tag color="red">Missing Sq_footage value in Aurora</Tag> : buildingWiseArr.modeCount33}
                                                            </>
                                                            }

                                                        </label> &nbsp;

                                                        {
                                                            detachedStructureBuildingIds.includes(Number(data.buildingName)) && (
                                                                <Tag color="blue">Detached Structure</Tag>)
                                                        }

                                                    </td>
                                                </tr>
                                            )}
                                            <tr key={data.roofFaceId} data-roofid={data.roofFaceId}>
                                                <td key="roofNum" align="center">{data.roofNum}</td>
                                                <td
                                                    key="pitch"
                                                    align="center"
                                                    onDoubleClick={() => {
                                                        if (!hasCapability('Override_Table')) {
                                                            sceneViewer.viewerAlert({
                                                                show: true,
                                                                title: "Error",
                                                                message: "You don't have permission to override pitch",
                                                                isCloseAfterDisplay: true,
                                                                messageType: "error"
                                                            });
                                                            return;
                                                        }

                                                        if(designList.length===0){
                                                            sceneViewer.viewerAlert({
                                                                show: true,
                                                                title: "Error",
                                                                message: "Cannot override pitch without saving at least one design/version",
                                                                isCloseAfterDisplay: true,
                                                                messageType: "error"
                                                            });
                                                            return;
                                                        }


                                                        if (pitchDisplayMode === 'Pitch Override') {
                                                            handleDoubleClick(data.roofFaceId, data.pitchOverride || data.pitch)
                                                        } else {
                                                            sceneViewer.viewerAlert({
                                                                show: true,
                                                                title: "Info",
                                                                message: "Cannot Edit Original Pitch , Select Adjusted from Pitch Dropdown to override pitch",
                                                                isCloseAfterDisplay: true,
                                                                messageType: "error"
                                                            });
                                                        }
                                                    }}
                                                >
                                                    {editablePitch[data.roofFaceId] ? (
                                                        <InputNumber
                                                            value={Math.round(currentEditingValue[data.roofFaceId])}
                                                            onChange={(value) => handlePitchChange(value as number, data.roofFaceId)}
                                                            onBlur={(e) => handleBlur(data.roofFaceId, data.pitch, data.roofNum, data.pitchOverride)}
                                                            autoFocus
                                                            onPressEnter={(e) => handleBlur(data.roofFaceId, data.pitch, data.roofNum, data.pitchOverride)}
                                                            onKeyDown={(e) => {
                                                                //Esc key
                                                                if (e.key==='Escape'){
                                                                    setEditablePitch((prev: any) => ({
                                                                        ...prev,
                                                                        [data.roofFaceId]: false
                                                                    }));
                                                                }
                                                            }}
                                                        />
                                                    ) : (
                                                        <span>
                                                        {Math.round(pitchDisplayMode === 'Pitch Override' ? (data.pitchOverride || data.pitch) : data.pitch)}
                                                    </span>
                                                    )}
                                                </td>
                                                {thArr.filter((item) => item.key !== 'roofNum').map((th, thIdx) => (
                                                    <td key={`${data.roofFaceId}-${th.key}`} align="center">
                                                        {th.key === 'arraySize' ? data[th.key] : Math.round(data[th.key])}
                                                    </td>
                                                ))}
                                            </tr>
                                        </React.Fragment>
                                    ))
                            ))}
                            </tbody>
                        </table>
                    </>
                )}
            </div>
        </div>
    );
};

export default RoofInformationTable;
